import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { CustomGraphLegend } from '../../../../SharedComponents/CustomGraphLegend';
import { CustomGraphTooltip } from '../../../../SharedComponents/CustomGraphTooltip';
import { GraphWrapper } from '../../../../SharedComponents/GraphWrapper';
import { QuarterCustomTick } from '../../../../SharedComponents/QuarterCustomTick';
import { roundUp } from '../../../../../utils/roundUp';
import { useMainLayout } from '../../../../layouts/MainPageLayout/MainLayoutContext';
import { formatLocalDate } from '../../../../../utils/localDateToDate';
import { Icon } from '@zillow/constellation-10';
import { IconArrowDownFilled, IconArrowUpFilled } from '@zillow/constellation-icons';
import type { VacancyRatesChartData } from '../MarketDataReport.data';
import { getGraphTitle } from '../../../../../utils/getGraphTitle';
import { format } from 'date-fns';

const getIconForChange = (change: number) => (
  <Icon>{change > 0 ? <IconArrowUpFilled /> : <IconArrowDownFilled />}</Icon>
);

type VacancyRatesGraphProps = {
  selectedRegionId: number;
  chartData: Array<VacancyRatesChartData>;
  isLoading: boolean;
  hasMoodysData: boolean;
};

export const VacancyRatesGraph = ({
  selectedRegionId,
  chartData,
  isLoading,
  hasMoodysData,
}: VacancyRatesGraphProps) => {
  const { filteredProperties, regionsMapping, selectedFilters, isFetchingInitialData, analyticsLastUpdatedDates } =
    useMainLayout();
  const lastUpdatedPortfolioDate = formatLocalDate(analyticsLastUpdatedDates?.portfolioPerformanceLastUpdated);
  const lastUpdatedMarketDate = formatLocalDate(analyticsLastUpdatedDates?.marketVacancyLastUpdated);

  const currentQuarterFormatted = format(new Date(), 'QQQ yyyy');
  const lastAvailableQuarter = chartData[chartData.length - 1];
  const portfolioAverage =
    lastAvailableQuarter?.['Portfolio average quarter to date'] ?? lastAvailableQuarter?.['Portfolio average'] ?? 0;
  const marketAverage =
    lastAvailableQuarter?.['Market average quarter to date'] ?? lastAvailableQuarter?.['Market average'] ?? 0;

  const lastTwoQuarters = chartData.slice(-2);
  let portfolioAverageChange = 0;
  let marketAverageChange = 0;

  if (lastTwoQuarters.length === 2) {
    const currentPortfolio =
      lastTwoQuarters[1]['Portfolio average quarter to date'] ?? lastTwoQuarters[1]['Portfolio average'] ?? 0;
    const previousPortfolio = lastTwoQuarters[0]['Portfolio average'] ?? 0;
    const currentMarket =
      lastTwoQuarters[1]['Market average quarter to date'] ?? lastTwoQuarters[1]['Market average'] ?? 0;
    const previousMarket = lastTwoQuarters[0]['Market average'] ?? 0;
    portfolioAverageChange = currentPortfolio - previousPortfolio;
    marketAverageChange = currentMarket - previousMarket;
  }

  const title = getGraphTitle({
    graphName: 'Vacancy rates',
    regionsMapping,
    selectedRegionId,
    selectedFilters,
    filteredProperties,
  });

  return (
    <GraphWrapper
      graphId="vacancy-rates"
      title={title}
      subtitle={`Vacancy rates based on the number of units advertised on Zillow Rentals Network.${lastUpdatedPortfolioDate ? ` Portfolio data updated ${lastUpdatedPortfolioDate}${hasMoodysData && lastUpdatedMarketDate ? `, market data updated ${lastUpdatedMarketDate}` : ''}.` : ''}`}
      loading={isLoading || isFetchingInitialData}
      infoCards={[
        {
          title: 'Portfolio average',
          infoText: 'Estimated average vacancy rate for your portfolio in the selected market, quarter to date.',
          value: `${portfolioAverage.toFixed(2)}%`,
          tag:
            Math.abs(portfolioAverageChange) > 0 ? (
              <>
                {getIconForChange(portfolioAverageChange)} {roundUp(portfolioAverageChange, 2)}% QoQ
              </>
            ) : undefined,
          tagTone: portfolioAverageChange < 0 ? 'success' : 'critical',
        },
        ...(hasMoodysData
          ? [
              {
                title: 'Market average',
                infoText: 'Estimated average vacancy rate for the selected market, forecasted for this quarter.',
                value: `${marketAverage.toFixed(2)}%`,
                tag:
                  Math.abs(marketAverageChange) > 0 ? (
                    <>
                      {getIconForChange(marketAverageChange)} {roundUp(marketAverageChange, 2)}% QoQ
                    </>
                  ) : undefined,
                tagTone: (marketAverageChange < 0 ? 'success' : 'critical') as 'critical' | 'success',
              },
            ]
          : []),
      ]}
    >
      <ResponsiveContainer width="100%" height={350}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="timestamp" interval={0} tick={<QuarterCustomTick />} />
          <YAxis tickFormatter={(value) => `${value}%`} />
          <Tooltip
            formatter={(value) => `${Number(value).toFixed(2)}%`}
            content={
              <CustomGraphTooltip
                filterPayload={
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (filteredPayload: Array<any>, displayLabel: string) => {
                    const isCurrentQuarter = displayLabel === currentQuarterFormatted;

                    if (isCurrentQuarter) {
                      return filteredPayload.filter(
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (entry: any) =>
                          entry.dataKey === 'Portfolio average quarter to date' ||
                          (hasMoodysData && entry.dataKey === 'Market average quarter to date'),
                      );
                    }

                    return filteredPayload.filter(
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (entry: any) =>
                        entry.dataKey === 'Portfolio average' || (hasMoodysData && entry.dataKey === 'Market average'),
                    );
                  }
                }
              />
            }
          />
          <Legend
            wrapperStyle={{ marginBottom: '-32px' }}
            content={
              <CustomGraphLegend
                labelTypes={{
                  'Portfolio average': 'line',
                  ...(hasMoodysData ? { 'Market average': 'line' as const } : {}),
                  'Quarter to date': 'dashed-line',
                }}
                filterPayload={
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (payload: Array<any>) => {
                    const filtered = payload.filter(
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (entry: any) =>
                        entry.value !== 'Portfolio average quarter to date' &&
                        entry.value !== 'Market average quarter to date' &&
                        (hasMoodysData || entry.value !== 'Market average'),
                    );
                    const order = ['Portfolio average', 'Market average', 'Quarter to date'];
                    return filtered.sort(
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (a: any, b: any) => order.indexOf(a.value) - order.indexOf(b.value),
                    );
                  }
                }
              />
            }
          />
          <Line
            type="linear"
            dataKey="Portfolio average quarter to date"
            stroke="#891EFA"
            strokeWidth={3}
            strokeDasharray="5 5"
            dot={false}
            legendType="none"
          />
          {hasMoodysData && (
            <Line
              type="linear"
              dataKey="Market average quarter to date"
              stroke="#009AAE"
              strokeWidth={3}
              strokeDasharray="5 5"
              dot={false}
              legendType="none"
            />
          )}
          <Line type="linear" dataKey="Quarter to date" stroke="#666666" strokeDasharray="5 5" dot={false} />
          <Line type="linear" dataKey="Portfolio average" stroke="#891EFA" strokeWidth={3} />
          {hasMoodysData && <Line type="linear" dataKey="Market average" stroke="#009AAE" strokeWidth={3} />}
        </LineChart>
      </ResponsiveContainer>
    </GraphWrapper>
  );
};
